import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getAccessToken, getAccessTokenExpiresIn, getRefreshToken, setTokens, clearTokens } from '../services/AuthSession';
import { login as apiLogin, googleLogin, me as apiMe, logout as apiLogout, refreshSession } from '../services/AuthService';
import { createGuestSession, logoutGuestSession } from '../services/AuthService';
import { clearGuestSession, getGuestSession, setGuestSession } from '../services/GuestSession';
import { clearRoomCredentials } from '../services/RoomSession';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [initializing, setInitializing] = useState(true);
    const [sessionUnavailable, setSessionUnavailable] = useState(false);
    const [guest, setGuest] = useState(() => getGuestSession());
    const refreshTimerRef = useRef(null);
    const authEpochRef = useRef(0);

    useEffect(() => {
        if (!guest) return undefined;
        const expire = () => { clearGuestSession(); setGuest(null); };
        const timeout = setTimeout(expire, Math.max(0, guest.expiresAt - Date.now()));
        window.addEventListener('pwf-guest-session-ended', expire);
        return () => { clearTimeout(timeout); window.removeEventListener('pwf-guest-session-ended', expire); };
    }, [guest]);

    const loadCurrentUser = useCallback(async () => {
        const epoch = authEpochRef.current;
        try {
            const profile = await apiMe();
            if (epoch !== authEpochRef.current || !getRefreshToken()) return null;
            setUser(profile);
            return profile;
        } catch (err) {
            // A transient profile-read failure must not log out a live room.
            if (err?.response?.status === 401 && !getRefreshToken()) setUser(null);
            return null;
        }
    }, []);

    // Schedule a proactive refresh a minute before the access token expires.
    const scheduleRefresh = useCallback((expiresIn) => {
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }
        const delay = Math.max((expiresIn - 60) * 1000, 30 * 1000);
        refreshTimerRef.current = setTimeout(async () => {
            if (document.visibilityState === 'hidden') return; // Foreground restoration renews when needed.
            try {
                const ok = await refreshSession();
                if (ok) scheduleRefresh(ok.expiresIn);
                else if (!getRefreshToken()) setUser(null);
            } catch (error) {
                if (getRefreshToken()) scheduleRefresh(60); // Retry in 30 seconds.
            }
        }, delay);
    }, []);

    // On mount: try to restore a session from the stored refresh token.
    useEffect(() => {
        let cancelled = false;
        let retryTimer;
        let restoring = false;
        const bootstrap = async () => {
            if (cancelled || restoring || document.visibilityState === 'hidden') return;
            const epoch = authEpochRef.current;
            clearTimeout(retryTimer);
            restoring = true;
            try {
                if (!getRefreshToken()) {
                    setGuest(getGuestSession());
                    if (!cancelled) setInitializing(false);
                    return;
                }
                const expiresIn = getAccessTokenExpiresIn();
                const result = getAccessToken() && (expiresIn == null || expiresIn > 60) ? null : await refreshSession();
                if (cancelled || epoch !== authEpochRef.current) return;
                if (result) scheduleRefresh(result.expiresIn);
                else if (getAccessToken()) scheduleRefresh(expiresIn ?? 86400);
                if (!getAccessToken() && getRefreshToken()) throw new Error('Session restoration was superseded; retry.');
                if (getAccessToken()) {
                    // Do not swallow a transient /me failure during restoration.
                    const profile = await apiMe();
                    if (cancelled || epoch !== authEpochRef.current || !getRefreshToken()) return;
                    setUser(profile);
                }
                if (!cancelled) {
                    setSessionUnavailable(false);
                    setInitializing(false);
                }
            } catch (error) {
                if (cancelled || epoch !== authEpochRef.current) return;
                if (!getRefreshToken()) {
                    setUser(null);
                    setInitializing(false);
                } else {
                    setSessionUnavailable(true);
                    retryTimer = setTimeout(bootstrap, 5000);
                }
            } finally {
                restoring = false;
            }
        };
        bootstrap();
        const ended = () => {
            authEpochRef.current += 1;
            setUser(null);
            setInitializing(false);
            setSessionUnavailable(false);
            clearTimeout(refreshTimerRef.current);
        };
        window.addEventListener('online', bootstrap);
        window.addEventListener('pwf-auth-session-ended', ended);
        window.addEventListener('pageshow', bootstrap);
        document.addEventListener('visibilitychange', bootstrap);
        return () => {
            cancelled = true;
            clearTimeout(retryTimer);
            window.removeEventListener('online', bootstrap);
            window.removeEventListener('pwf-auth-session-ended', ended);
            window.removeEventListener('pageshow', bootstrap);
            document.removeEventListener('visibilitychange', bootstrap);
            if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        };
    }, [scheduleRefresh]);

    const applySession = useCallback((tokenResponse) => {
        authEpochRef.current += 1;
        setTokens(tokenResponse.accessToken, tokenResponse.refreshToken);
        setUser(tokenResponse.player || null);
        clearGuestSession(); setGuest(null);
        setInitializing(false);
        setSessionUnavailable(false);
        scheduleRefresh(tokenResponse.expiresIn);
        return tokenResponse.player;
    }, [scheduleRefresh]);

    const login = useCallback(async (identifier, password) => {
        const result = await apiLogin(identifier, password);
        return applySession(result);
    }, [applySession]);

    const loginWithGoogle = useCallback(async (idToken) => {
        const result = await googleLogin(idToken);
        if (result?.requiresProfile) {
            return result; // Caller finishes registration via google/complete.
        }
        if (result?.token) {
            applySession(result.token);
        }
        return result;
    }, [applySession]);

    const logout = useCallback(async () => {
        authEpochRef.current += 1;
        const refreshToken = getRefreshToken();
        try {
            if (refreshToken) {
                await apiLogout(refreshToken);
            }
        } catch (err) {
            // Best effort: clear local state regardless.
        }
        clearTokens();
        clearRoomCredentials();
        setUser(null);
        setInitializing(false);
        setSessionUnavailable(false);
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    }, []);

    const playAsGuest = useCallback(async () => {
        const existing = getGuestSession();
        if (existing) { setGuest(existing); return existing; }
        const result = await createGuestSession();
        const value = { accessToken: result.accessToken, playerId: result.playerId, expiresAt: Date.now() + result.expiresIn * 1000 };
        setGuestSession(value); setGuest(value); return value;
    }, []);

    const logoutGuest = useCallback(async () => {
        try { await logoutGuestSession(); } catch (_) { /* local logout remains authoritative for this browser */ }
        clearGuestSession(); clearRoomCredentials(); setGuest(null);
    }, []);

    const value = {
        user,
        initializing,
        sessionUnavailable,
        isAuthenticated: !!user || !!getAccessToken(),
        guest,
        isGuest: !!guest,
        hasPlayerSession: !!user || !!getAccessToken() || !!guest,
        login,
        loginWithGoogle,
        logout,
        applySession,
        refreshUser: loadCurrentUser,
        setUser,
        playAsGuest,
        logoutGuest,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
